import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ActivityIndicator,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../contexts/ThemeContext';
import { getColors, typography, spacing, borderRadius } from '../styles/theme';
import { createClubSubscription } from '../services/subscriptionService';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_CONFIG } from '../config/stripe';

let stripePromise;
const getStripe = () => {
  if (!stripePromise && Platform.OS === 'web') {
    stripePromise = loadStripe(STRIPE_CONFIG.publishableKey);
  }
  return stripePromise;
};

// Inner component that uses Stripe Elements (web only)
const PaymentForm = ({ clientSecret, onSuccess, onError, email, nameOnCard, colors, styles }) => {
  const [loading, setLoading] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const cardElementRef = React.useRef(null);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      getStripe().then((stripeInstance) => {
        if (stripeInstance) {
          setStripe(stripeInstance);
          // Create Stripe Elements directly (no React wrapper needed)
          const stripeElements = stripeInstance.elements();
          const card = stripeElements.create('card', {
            style: {
              base: {
                fontSize: '16px',
                color: colors.text,
                fontFamily: 'system-ui, -apple-system, sans-serif',
                '::placeholder': {
                  color: colors.textLight || '#aab7c4',
                },
              },
              invalid: {
                color: '#fa755a',
              },
            },
          });
          
          // Mount the card element when the DOM is ready
          setTimeout(() => {
            const elementContainer = document.getElementById('card-element');
            if (elementContainer) {
              card.mount('#card-element');
              setCardElement(card);
              cardElementRef.current = card;
            } else {
              console.error('Card element container not found');
              onError('Payment form container not found. Please refresh the page.');
            }
          }, 100);
        }
      }).catch((err) => {
        console.error('Failed to initialize Stripe:', err);
        onError('Failed to load payment form. Please refresh the page.');
      });
    }

    return () => {
      if (cardElementRef.current) {
        try {
          cardElementRef.current.unmount();
        } catch (e) {
          // Ignore unmount errors
        }
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (!stripe || !cardElement) {
      onError('Payment form not ready. Please wait a moment.');
      return;
    }

    setLoading(true);

    try {
      // Create payment method from card element
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: nameOnCard || email,
          email: email,
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Confirm subscription payment
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod.id,
        }
      );

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      onSuccess(paymentIntent);
    } catch (err) {
      onError(err.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (Platform.OS !== 'web') {
    return (
      <View>
        <Text style={{ color: colors.error }}>Subscriptions are only supported on web.</Text>
      </View>
    );
  }

  return (
    <View>
      {Platform.OS === 'web' && typeof document !== 'undefined' ? (
        <>
          <View style={{ marginBottom: spacing.md }}>
            <Text style={{ fontSize: typography.bodySmall, color: colors.textSecondary, marginBottom: spacing.xs }}>
              Card Information
            </Text>
            <div 
              id="card-element" 
              style={{ 
                padding: `${spacing.md}px`, 
                backgroundColor: colors.backgroundDark, 
                borderRadius: `${borderRadius.md}px`,
                border: `1px solid ${colors.border}`,
                minHeight: '50px',
              }} 
            />
          </View>
        </>
      ) : (
        <Text style={{ color: colors.error }}>Stripe Elements require web platform</Text>
      )}

      <TouchableOpacity
        style={styles.payButton}
        onPress={handleSubmit}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator size="small" color="#FFF" />
        ) : (
          <>
            <Icon name="lock" size={20} color="#FFF" />
            <Text style={styles.payButtonText}>Subscribe Now</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );
};

const SubscriptionPaymentModal = ({ 
  visible, 
  onClose, 
  onSuccess,
  userId,
  email,
  clubName,
  amount = 30,
}) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('confirm'); // 'confirm' or 'payment'
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [nameOnCard, setNameOnCard] = useState('');

  const handlePaymentSuccess = (paymentIntent) => {
    setLoading(false);
    if (onSuccess && subscriptionData) {
      onSuccess({
        subscriptionId: subscriptionData.subscriptionId,
        customerId: subscriptionData.customerId,
        paymentIntent,
      });
    }
    onClose();
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
    setLoading(false);
  };

  const handleContinueToPayment = async () => {
    if (Platform.OS !== 'web') {
      setError('Subscriptions are currently only supported on web. Please use a web browser.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create subscription first to get clientSecret
      const subscriptionResult = await createClubSubscription(
        userId,
        email,
        clubName
      );
      
      setClientSecret(subscriptionResult.clientSecret);
      setSubscriptionData({
        subscriptionId: subscriptionResult.subscriptionId,
        customerId: subscriptionResult.customerId,
      });
      setStep('payment');
      setLoading(false);
    } catch (err) {
      console.error('Subscription creation error:', err);
      setError(err.message || 'Failed to create subscription. Please try again.');
      setLoading(false);
    }
  };

  const styles = createStyles(colors);

  if (step === 'confirm') {
    return (
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={onClose}
      >
        <View style={styles.overlay}>
          <View style={styles.modal}>
            <View style={styles.header}>
              <Text style={styles.title}>Upgrade to Club Tier</Text>
              <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                <Icon name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>

            <View style={[styles.content, { overflow: 'hidden' }]}>
              <View style={styles.priceSection}>
                <Text style={styles.priceAmount}>${amount}</Text>
                <Text style={styles.pricePeriod}>per month</Text>
              </View>

              <View style={styles.featuresList}>
                <Text style={styles.featuresTitle}>What you'll get:</Text>
                {[
                  'Create unlimited events',
                  'Collect payments for events',
                  'Advanced analytics dashboard',
                  'Promote your club to players',
                  'Manage club members',
                  'Host official tournaments',
                  'Priority customer support',
                  'Verified club badge',
                ].map((feature, index) => (
                  <View key={index} style={styles.featureItem}>
                    <Icon name="check-circle" size={20} color={colors.primary} />
                    <Text style={styles.featureText}>{feature}</Text>
                  </View>
                ))}
              </View>

              <View style={styles.infoBox}>
                <Icon name="info" size={20} color={colors.info} />
                <Text style={styles.infoText}>
                  You can cancel anytime. Your subscription will renew automatically each month.
                </Text>
              </View>

              {error ? (
                <View style={styles.errorBox}>
                  <Text style={styles.errorText}>{error}</Text>
                </View>
              ) : null}
            </View>

            <View style={styles.footer}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={onClose}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.button, styles.continueButton]}
                onPress={handleContinueToPayment}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.continueButtonText}>Continue to Payment</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  // Payment step
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <TouchableOpacity onPress={() => setStep('confirm')} style={styles.backButton}>
              <Icon name="arrow-back" size={24} color={colors.text} />
            </TouchableOpacity>
            <Text style={styles.title}>Payment Details</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Icon name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          <View style={[styles.content, { overflow: 'hidden' }]}>
            {Platform.OS === 'web' && typeof window !== 'undefined' ? (
              <>
                <PaymentForm
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  email={email}
                  nameOnCard={nameOnCard}
                  colors={colors}
                  styles={styles}
                />
                <View style={styles.testCardBox}>
                  <Icon name="code" size={18} color={colors.warning} />
                  <Text style={styles.testCardText}>
                    Test card: 4242 4242 4242 4242, any future date, any CVC
                  </Text>
                </View>
              </>
            ) : (
              <Text style={{ color: colors.error }}>
                Subscriptions are only supported on web. Please use a web browser.
              </Text>
            )}

            <View style={styles.totalSection}>
              <Text style={styles.totalLabel}>Total due today:</Text>
              <Text style={styles.totalAmount}>${amount}.00</Text>
            </View>

            {error ? (
              <View style={styles.errorBox}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}
          </View>

          <Text style={styles.secureText}>
            <Icon name="lock" size={12} color={colors.textSecondary} />
            {' '}Secure payment powered by Stripe
          </Text>
        </View>
      </View>
    </Modal>
  );
};

const createStyles = (colors) => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
    ...(Platform.OS === 'web' && {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
    }),
  },
  modal: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.xl,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  backButton: {
    padding: spacing.xs,
  },
  title: {
    fontSize: typography.h4,
    fontWeight: typography.bold,
    color: colors.text,
    flex: 1,
    textAlign: 'center',
  },
  closeButton: {
    padding: spacing.xs,
  },
  content: {
    padding: spacing.lg,
  },
  priceSection: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
  },
  priceAmount: {
    fontSize: 48,
    fontWeight: typography.bold,
    color: colors.primary,
  },
  pricePeriod: {
    fontSize: typography.body,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  featuresList: {
    marginVertical: spacing.lg,
  },
  featuresTitle: {
    fontSize: typography.h6,
    fontWeight: typography.semiBold,
    color: colors.text,
    marginBottom: spacing.md,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  featureText: {
    fontSize: typography.body,
    color: colors.text,
    flex: 1,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    padding: spacing.md,
    backgroundColor: colors.backgroundDark,
    borderRadius: borderRadius.md,
    marginTop: spacing.md,
  },
  infoText: {
    fontSize: typography.bodySmall,
    color: colors.textSecondary,
    flex: 1,
    lineHeight: 20,
  },
  errorBox: {
    backgroundColor: '#FFE5E5',
    borderLeftWidth: 4,
    borderLeftColor: '#FF4444',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginTop: spacing.md,
  },
  errorText: {
    color: '#CC0000',
    fontSize: typography.bodySmall,
    fontWeight: typography.medium,
  },
  footer: {
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: borderRadius.lg,
  },
  cancelButton: {
    backgroundColor: colors.backgroundDark,
  },
  cancelButtonText: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: colors.textSecondary,
  },
  continueButton: {
    backgroundColor: colors.primary,
  },
  continueButtonText: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: '#FFF',
  },
  testCardBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    backgroundColor: '#FFF9E6',
    borderRadius: borderRadius.md,
    marginTop: spacing.sm,
  },
  testCardText: {
    fontSize: typography.caption,
    color: '#B8860B',
    flex: 1,
  },
  totalSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.backgroundDark,
    borderRadius: borderRadius.md,
    marginTop: spacing.lg,
  },
  totalLabel: {
    fontSize: typography.h6,
    fontWeight: typography.semiBold,
    color: colors.text,
  },
  totalAmount: {
    fontSize: typography.h4,
    fontWeight: typography.bold,
    color: colors.primary,
  },
  payButton: {
    backgroundColor: colors.primary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    borderRadius: borderRadius.lg,
    marginTop: spacing.md,
  },
  payButtonText: {
    fontSize: typography.h6,
    fontWeight: typography.bold,
    color: '#FFF',
  },
  secureText: {
    fontSize: typography.caption,
    color: colors.textSecondary,
    textAlign: 'center',
    paddingBottom: spacing.md,
  },
});

export default SubscriptionPaymentModal;
